import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  alpha,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch, type GlobalSearchResult } from '@/hooks/queries/useGlobalSearch';
import { useEntityTypes, useMenuConfig } from '@/hooks';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { buildMenu, type MenuGroup, type MenuLeaf } from '@/utils/navigation';
import { multiWordMatch } from '@/utils/multiWordMatch';

/** Distinct hex colors for each search result category */
const CATEGORY_COLORS: Record<string, string> = {
  Asset: '#1976d2',           // blue
  'Bulk Inventory': '#0d47a1', // dark blue
  Person: '#2e7d32',          // green
  Catalog: '#ed6c02',         // orange
  'Barcode Label': '#e65100', // deep orange
  Location: '#00838f',        // teal
  'Hand Receipt': '#6a1b9a',  // purple
  'IE Kit': '#4527a0',       // deep purple
  Transaction: '#0277bd',     // light blue
  'Asset Transaction': '#01579b', // dark light-blue
  'Transfer / Shipment': '#00695c', // dark teal
  'Maintenance Request': '#bf360c', // brown-red
  Maintenance: '#bf360c',     // brown-red
  'Person Sizing': '#1b5e20', // dark green
  'Purchase Requisition': '#f9a825', // amber
  'Loss Report': '#c62828',   // red
  'Saved Cart': '#6d4c41',    // brown
  'IAR Run': '#5c6bc0',       // indigo
  Report: '#7b1fa2',          // purple
  Navigation: '#546e7a',      // blue-grey
};

const getCategoryColor = (category: string, ieKitLabel?: string): string => {
  if (ieKitLabel && category === ieKitLabel) return CATEGORY_COLORS['IE Kit'] ?? '#757575';
  return CATEGORY_COLORS[category] ?? '#757575';
};

export const GlobalSearchBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { label: ieKitLabel } = useIeKitLabels('global_search');
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: results = [], isFetching } = useGlobalSearch(inputValue);

  // Build navigation menu items for inline search
  const { data: entityTypes } = useEntityTypes();
  const { data: uiMenu } = useMenuConfig();

  const menuResults = useMemo<GlobalSearchResult[]>(() => {
    const query = inputValue.trim();
    if (!query || !entityTypes) return [];
    const items = buildMenu(entityTypes, uiMenu ?? null);
    const leaves: Array<{ key: string; label: string; route: string; group?: string }> = [];
    items.forEach((it) => {
      if ((it as MenuLeaf).kind === 'leaf') {
        const leaf = it as MenuLeaf;
        leaves.push({ key: leaf.key, label: leaf.label, route: leaf.route });
      } else {
        const g = it as MenuGroup;
        g.children.forEach((leaf) =>
          leaves.push({ key: leaf.key, label: leaf.label, route: leaf.route, group: g.label }),
        );
      }
    });
    return leaves
      .filter((l) => multiWordMatch(`${l.label} ${l.key} ${l.group ?? ''}`, query))
      .map((l) => ({
        id: `nav-${l.key}`,
        category: 'Navigation',
        display_name: l.label,
        secondary: l.group ? `in ${l.group}` : undefined,
        route: l.route,
      }));
  }, [inputValue, entityTypes, uiMenu]);

  const mergedResults = useMemo<GlobalSearchResult[]>(
    () => [...menuResults, ...results],
    [results, menuResults],
  );

  const hasInput = inputValue.trim().length > 0;

  // Ctrl+K / Cmd+K keyboard shortcut to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, value: string | GlobalSearchResult | null) => {
      if (!value || typeof value === 'string') return;

      navigate(value.route);

      // Reset after navigation
      setInputValue('');
      setOpen(false);
      inputRef.current?.blur();
    },
    [navigate],
  );

  return (
    <Autocomplete<GlobalSearchResult, false, true, true>
      freeSolo
      disableClearable
      open={open && hasInput}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={inputValue}
      onInputChange={(_e, value, reason) => {
        if (reason !== 'reset') setInputValue(value);
      }}
      options={mergedResults}
      loading={isFetching}
      filterOptions={(x) => x} // server-side filtering
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.display_name
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={handleSelect}
      noOptionsText={
        isFetching ? 'Searching…' : 'No results found'
      }
      sx={{ width: '100%' }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const color = getCategoryColor(option.category, ieKitLabel);
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{
              gap: 1,
              backgroundColor: alpha(color, 0.06),
              borderLeft: `3px solid ${color}`,
              '&:hover': { backgroundColor: alpha(color, 0.12) },
            }}
          >
            <Chip
              label={option.category}
              size="small"
              sx={{
                minWidth: 110,
                justifyContent: 'center',
                backgroundColor: alpha(color, 0.15),
                color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                fontWeight: 600,
                borderColor: color,
              }}
              variant="outlined"
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.display_name}
              </Typography>
              {option.secondary && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {option.secondary}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Search the eQuip Asset Management System"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              borderRadius: 2,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255,255,255,0.7)',
              opacity: 1,
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isFetching && hasInput ? (
                    <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                  ) : null}
                  {!inputValue && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.5)',
                        whiteSpace: 'nowrap',
                        mr: 1,
                        display: { xs: 'none', sm: 'block' },
                      }}
                    >
                      Ctrl+K
                    </Typography>
                  )}
                </>
              ),
            },
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: { mt: 1, minWidth: 420 },
        },
      }}
    />
  );
};
